import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './styles.css';
import { api, HttpError } from './lib/api';
import { MeCtx, type Me } from './lib/me';
import { logout, onSession, type Session } from './lib/firebase';
import { DirectoryProvider, linkIfNeeded } from './lib/directory';
import { Shell } from './components/Shell';
import { ToastProvider } from './components/ui';
import { Enroll, Login } from './pages/Login';
import { StepUpProvider } from './components/StepUp';
import { MapPage } from './pages/MapPage';
import { DeliveriesPage } from './pages/DeliveriesPage';
import { DispatchPage } from './pages/DispatchPage';
import { DriversPage } from './pages/DriversPage';
import { RollCallPage } from './pages/RollCallPage';
import { IssuesPage } from './pages/IssuesPage';
import { ChatPage } from './pages/ChatPage';
import { SchedulePage } from './pages/SchedulePage';
import { TimelinePage } from './pages/TimelinePage';
import { RoutinesPage } from './pages/RoutinesPage';
import { ClientsPage } from './pages/ClientsPage';
import { DriverDetailPage } from './pages/DriverDetailPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { RecordsPage } from './pages/RecordsPage';
import { StatusPage } from './pages/StatusPage';
import { PlacesPage } from './pages/PlacesPage';
import { AuditPage } from './pages/AuditPage';
import { SystemPage } from './pages/SystemPage';
import { StaffPage } from './pages/StaffPage';

function App() {
  const [me, setMe] = useState<Me | null | 'none'>(null);
  const [session, setSession] = useState<Session>({ state: 'out' });
  const [error, setError] = useState('');
  const fail = async (msg: string) => { setError(msg); await logout(); setMe('none'); };
  useEffect(() => onSession(async (s) => {
    setSession(s);
    if (s.state === 'out') { setMe('none'); return; }
    // 初めてのログインでは、登録されたアドレスと同じなら自分を名簿にひもづける
    const linked = await linkIfNeeded(s).catch(() => false);
    if (!linked) { await fail('このアカウントは登録されていません。運行管理者に依頼してください'); return; }
    if (s.state === 'enroll') { setMe('none'); return; }
    try { setMe(await api.get<Me>('/v1/office/me')); setError(''); }
    catch (e) { await fail(e instanceof HttpError ? e.message : 'ログインできませんでした'); }
  }), []);
  if (me === null) return null;
  if (session.state === 'enroll') return <Enroll email={session.email} />;
  const shell = me === 'none' ? <Navigate to="/login" replace /> : <DirectoryProvider officeId={me.officeId}><StepUpProvider><Shell /></StepUpProvider></DirectoryProvider>;
  return (
    <MeCtx.Provider value={me === 'none' ? null : me}>
      <Routes>
        <Route path="/login" element={me === 'none' ? <Login error={error} /> : <Navigate to="/" replace />} />
        <Route element={shell}>
          <Route path="/" element={<MapPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/deliveries" element={<DeliveriesPage />} />
          <Route path="/dispatch" element={<DispatchPage />} />
          <Route path="/drivers" element={<DriversPage />} />
          <Route path="/drivers/:id" element={<DriverDetailPage />} />
          <Route path="/roll-calls" element={<RollCallPage />} />
          <Route path="/issues" element={<IssuesPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/timeline" element={<TimelinePage />} />
          <Route path="/routines" element={<RoutinesPage />} />
          <Route path="/vehicles" element={<VehiclesPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/records" element={<RecordsPage />} />
          <Route path="/places" element={<PlacesPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/system" element={<SystemPage />} />
          <Route path="/staff" element={<StaffPage />} />
        </Route>
      </Routes>
    </MeCtx.Provider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode><BrowserRouter><ToastProvider><App /></ToastProvider></BrowserRouter></StrictMode>,
);
